/**
 * AudioEngine. Tone.js bed loop + FX rack + analyser tap.
 * Owns all audio: loads mode assets, drives the bed loop via SampleBank,
 * exposes the parameter surface from spec §4.2, and provides a getLevel() tap.
 * Never touches three.js, PointerEvents, or DOM.
 */

import * as Tone from 'tone';
import { createFxRack, type FxRack } from '../audio/fx-rack';
import { SampleBank } from '../audio/sample-bank';
import type { AudioParams, AudioLevels, ModeConfig, ModesFile } from '../types';

// ─── Constants ─────────────────────────────────────────────────────────────

/** 30ms ramp time — avoids zipper noise on every setter (spec §5). */
const RAMP_S = 0.03;
const INSTRUMENT_GATE_ATTACK_S = 0.04;
const INSTRUMENT_GATE_RELEASE_S = 0.55;

/**
 * Minimum delta below which apply() skips the corresponding setter. Tuned so
 * subtle drift (LFOs, smooth idle evolution) still updates, but per-frame
 * jitter from inactive params doesn't burn ~360 Tone scheduling ops/sec.
 */
const APPLY_EPSILON = 0.002;

/** FFT analyser size — power-of-two per Web Audio spec. */
const FFT_SIZE = 1024;

/** Max time to wait for a Tone.Player load callback before boot continues. */
const PLAYER_LOAD_TIMEOUT_MS = 5_000;

/** Assumed sample rate for FFT bin → Hz mapping. */
const SAMPLE_RATE = 44100;

/** Frequency boundaries for bass / mid / treble bands (Hz). */
const BAND_BASS_MAX   = 250;
const BAND_MID_MAX    = 2000;
const BAND_TREBLE_MAX = 8000;

/** Clamp helper — keeps values in [0, 1]. */
function clamp01(v: number): number {
  return Math.max(0, Math.min(1, v));
}

/** Convert a dBFS value (typically -∞..0) to a linear 0..1 level. */
function dbToLinear01(db: number): number {
  // Map [-80, 0] dBFS → [0, 1]
  return clamp01((db + 80) / 80);
}

/** Average a slice of Float32Array, returning a value in 0..1 via dbToLinear01. */
function avgBand(data: Float32Array, startBin: number, endBin: number): number {
  const start = Math.max(0, startBin);
  const end   = Math.min(data.length - 1, endBin);
  if (start > end) return 0;
  let sum = 0;
  for (let i = start; i <= end; i++) sum += data[i];
  return dbToLinear01(sum / (end - start + 1));
}

/** Convert frequency in Hz to FFT bin index. */
function hzToBin(hz: number, fftSize: number, sampleRate: number): number {
  return Math.round((hz / (sampleRate / 2)) * (fftSize / 2));
}

// ─── AudioEngine ───────────────────────────────────────────────────────────

export interface AudioEngineOptions {
  context?:  AudioContext;
  modesUrl?: string;
}

export class AudioEngine {
  // ── Private state ─────────────────────────────────────────────────────
  private readonly modesUrl: string;
  private modes: ModeConfig[] | null = null;
  private currentModeIndex: 0 | 1 | 2 = 0;

  /** FX chain (FeedbackDelay → Vibrato → BitCrusher → Chebyshev → Filter → Reverb). */
  private readonly rack: FxRack;

  /** Bed-loop player for the current mode. */
  private bedPlayer: Tone.Player | null = null;

  /** Whether the session has requested continuous bed playback. */
  private wantsPlayback = false;

  /** Oneshot sample bank for the current mode. */
  private bank: SampleBank;

  /** FFT analyser for getLevel(). */
  private readonly fftAnalyser: Tone.Analyser;

  /** RMS analyser for getLevel().rms. */
  private readonly rmsAnalyser: Tone.Analyser;

  /** Touch-controlled input gate. Open only while the user is touching the instrument. */
  private readonly instrumentGate: Tone.Gain;

  /** Prevents the rAF loop from scheduling duplicate Tone ramps every frame. */
  private instrumentGateOpen = false;

  constructor(opts: AudioEngineOptions = {}) {
    this.modesUrl   = opts.modesUrl ?? '/media/modes.json';
    this.rack       = createFxRack();
    this.bank       = new SampleBank();
    this.fftAnalyser = new Tone.Analyser({ type: 'fft',      size: FFT_SIZE, smoothing: 0.8 });
    this.rmsAnalyser = new Tone.Analyser({ type: 'waveform', size: 256,      smoothing: 0 });
    this.instrumentGate = new Tone.Gain(0);
    // Wire touch gate before the time-based FX so releases can ring out
    // naturally instead of chopping the processed output.
    this.instrumentGate.connect(this.rack.input);
    this.rack.output.connect(this.fftAnalyser);
    this.rack.output.connect(this.rmsAnalyser);
    this.rack.output.connect(Tone.getDestination() as unknown as Tone.ToneAudioNode);
  }

  // ─── Mode loading ──────────────────────────────────────────────────────

  /**
   * Fetches modes.json (once) and loads the bed loop + oneshot bank for the
   * given mode index. Safe to call multiple times; re-loads on index change.
   */
  async loadMode(index: 0 | 1 | 2): Promise<void> {
    if (!this.modes) {
      const res   = await fetch(this.modesUrl);
      const data  = (await res.json()) as ModesFile;
      this.modes  = data.modes;
    }
    const mode = this.modes[index];
    if (!mode) return;

    this.currentModeIndex = index;

    // Dispose existing bed player if any
    if (this.bedPlayer) {
      this.bedPlayer.dispose();
      this.bedPlayer = null;
    }

    // Create and connect the bed loop player. Await its buffer load so the
    // subsequent start() call doesn't throw "buffer is either not set or not
    // loaded" — same fake-async trap SampleBank had.
    const player: Tone.Player = await new Promise((resolve) => {
      let p: Tone.Player;
      let settled = false;
      const finish = (p: Tone.Player) => {
        if (settled) return;
        settled = true;
        clearTimeout(timeout);
        resolve(p);
      };
      const timeout = setTimeout(() => {
        // eslint-disable-next-line no-console
        console.warn(`AudioEngine: bed loop ${mode.bed} did not load within ${PLAYER_LOAD_TIMEOUT_MS}ms`);
        finish(p);
      }, PLAYER_LOAD_TIMEOUT_MS);
      p = new Tone.Player({
        url: mode.bed,
        loop: true,
        onload: () => {
          finish(p);
          this.startBedPlayer(p);
        },
        onerror: (err) => {
          // eslint-disable-next-line no-console
          console.warn(`AudioEngine: bed loop ${mode.bed} failed to load`, err);
          finish(p); // resolve anyway so loadMode doesn't hang; start() guards
        },
      });
      // Test mocks set loaded=true synchronously and never invoke onload.
      if ((p as unknown as { loaded?: boolean }).loaded) finish(p);
    });
    player.connect(this.instrumentGate);
    this.bedPlayer = player;
    this.startBedPlayer(player);

    // Load oneshot bank
    await this.bank.dispose();
    this.bank = new SampleBank();
    await this.bank.load(mode.oneshots);
  }

  /** Set the current mode index (does NOT reload assets — call loadMode for that). */
  setMode(index: 0 | 1 | 2): void {
    this.currentModeIndex = index;
  }

  /** Return the current mode index. */
  getModeIndex(): 0 | 1 | 2 {
    return this.currentModeIndex;
  }

  // ─── Parameter surface (spec §4.2) ─────────────────────────────────────

  /** 0..2 → bed loop playback rate. */
  setBedRate(rate: number): void {
    if (!this.bedPlayer) return;
    // Tone v15: Player.playbackRate is a plain number, not a Param. Direct
    // assignment is the only API. The unit-test mock decorates playbackRate
    // with a {rampTo} shape — without this dual-shape branch the runtime
    // crashes with "rampTo is not a function" on the real Tone build, and the
    // mock-only path hid it.
    const pr = (this.bedPlayer as unknown as { playbackRate: unknown }).playbackRate;
    if (pr && typeof (pr as { rampTo?: unknown }).rampTo === 'function') {
      // Test environment — mocks still get to spy on rampTo.
      (pr as { rampTo: (v: number, t: number) => void }).rampTo(rate, RAMP_S);
    } else {
      // Real Tone v15 — direct number assignment.
      (this.bedPlayer as unknown as { playbackRate: number }).playbackRate = rate;
    }
  }

  /** 0..1 → FeedbackDelay feedback amount, ramped 30ms. */
  setFeedback(amount: number): void {
    this.rack.setFeedback(amount);
  }

  /** 0..1 → Filter cutoff (log-mapped 200–8000 Hz), ramped 30ms. */
  setFilter(cutoff01: number): void {
    this.rack.setFilter(cutoff01);
  }

  /** 0..1 → L/R detune via Vibrato depth + frequency, ramped 30ms. */
  setChroma(amount: number): void {
    this.rack.setChroma(amount);
  }

  /** 0..1 → Reverb wet mix, ramped 30ms. */
  setSpace(wet: number): void {
    this.rack.setSpace(wet);
  }

  /** Opens while touching, closes after release. Keeps players running silently. */
  setInstrumentGate(active: boolean): void {
    if (this.instrumentGateOpen === active) return;
    this.instrumentGateOpen = active;
    if (active) {
      this.instrumentGate.gain.rampTo(1, INSTRUMENT_GATE_ATTACK_S);
    } else {
      this.instrumentGate.gain.targetRampTo(0, INSTRUMENT_GATE_RELEASE_S);
    }
  }

  /**
   * Apply a whole AudioParams in one call.
   *
   * Per-frame throttle: each setter wraps a Tone rampTo (3 of them) plus a
   * Player.playbackRate write. At 60Hz that's ~360 audio scheduling ops/sec
   * even when nothing changes — the dominant CPU cost of the rAF loop. We
   * skip a setter if its delta from the last applied value is below
   * APPLY_EPSILON; the param will catch up on the next non-trivial frame.
   */
  apply(params: AudioParams): void {
    const last = this._lastApplied;
    if (!last || Math.abs(params.bedRate - last.bedRate) > APPLY_EPSILON) {
      this.setBedRate(params.bedRate);
    }
    if (!last || Math.abs(params.feedback - last.feedback) > APPLY_EPSILON) {
      this.setFeedback(params.feedback);
    }
    if (!last || Math.abs(params.filter - last.filter) > APPLY_EPSILON) {
      this.setFilter(params.filter);
    }
    if (!last || Math.abs(params.chroma - last.chroma) > APPLY_EPSILON) {
      this.setChroma(params.chroma);
    }
    if (!last || Math.abs(params.space - last.space) > APPLY_EPSILON) {
      this.setSpace(params.space);
    }
    this._lastApplied = { ...params };
  }
  private _lastApplied: AudioParams | null = null;

  // ─── Oneshots ──────────────────────────────────────────────────────────

  /** Trigger the oneshot player at the given index. No-op if out of range or buffer not yet loaded. */
  injectOneshot(index: number): void {
    const player = this.bank.get(index);
    if (!player) return;
    // Tone v15 throws "buffer is either not set or not loaded" if start() is
    // called before the audio buffer has decoded. MachineMapper emits inject
    // events from the very first frame (tap, double-tap, idle evolution), so
    // a guard is required — otherwise the throw escapes frame() and kills the
    // entire rAF loop on the first inject.
    if (!(player as unknown as { loaded?: boolean }).loaded) return;
    try {
      player.start();
    } catch {
      // Swallow any residual Tone error — never let an oneshot kill the loop.
    }
  }

  // ─── Analyser tap ──────────────────────────────────────────────────────

  /**
   * Returns AudioLevels { bass, mid, treble, rms } — all values clamped to [0, 1].
   * FFT bins are averaged per band; RMS is computed from the waveform buffer.
   */
  getLevel(): AudioLevels {
    const fftData = this.fftAnalyser.getValue() as Float32Array;

    const bassBin   = hzToBin(BAND_BASS_MAX,   FFT_SIZE, SAMPLE_RATE);
    const midBin    = hzToBin(BAND_MID_MAX,     FFT_SIZE, SAMPLE_RATE);
    const trebleBin = hzToBin(BAND_TREBLE_MAX,  FFT_SIZE, SAMPLE_RATE);

    const bass   = avgBand(fftData, 0,           bassBin);
    const mid    = avgBand(fftData, bassBin + 1, midBin);
    const treble = avgBand(fftData, midBin + 1,  trebleBin);

    // RMS from waveform analyser
    const waveData = this.rmsAnalyser.getValue() as Float32Array;
    let sumSq = 0;
    for (let i = 0; i < waveData.length; i++) sumSq += waveData[i] * waveData[i];
    const rms = clamp01(Math.sqrt(sumSq / waveData.length));

    return { bass, mid, treble, rms };
  }

  // ─── Recorder tap ──────────────────────────────────────────────────────

  /**
   * Route a copy of the audible post-FX output into a MediaStreamAudioDestinationNode so
   * the Recorder can capture audible audio. Safe to call multiple times — the
   * Tone graph happily fans out to N destinations. Connects in parallel to the
   * existing Tone.getDestination() route.
   */
  connectRecorderTap(destination: MediaStreamAudioDestinationNode): void {
    this.rack.output.connect(destination as unknown as Tone.ToneAudioNode);
  }

  // ─── Lifecycle ─────────────────────────────────────────────────────────

  /** Unlock audio context, start Transport and bed loop. */
  async start(): Promise<void> {
    this.wantsPlayback = true;
    await Tone.start();
    Tone.Transport.start();
    this.startBedPlayer();
  }

  /** Pause the Transport (preserves position). */
  pause(): void {
    this.wantsPlayback = false;
    Tone.Transport.pause();
  }

  /** Release all Tone nodes and players. */
  dispose(): void {
    this.wantsPlayback = false;
    this.rack.dispose();
    this.bank.dispose();
    if (this.bedPlayer) {
      this.bedPlayer.dispose();
      this.bedPlayer = null;
    }
    this.fftAnalyser.dispose();
    this.rmsAnalyser.dispose();
    this.instrumentGate.dispose();
  }

  private startBedPlayer(player = this.bedPlayer): void {
    if (!this.wantsPlayback || !player || !(player as unknown as { loaded?: boolean }).loaded) return;
    if ((player as unknown as { state?: string }).state === 'started') return;
    try {
      player.start();
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('bedPlayer.start() failed', err);
    }
  }
}
